import { CURRENCY_3_LETTER, CURRENCY_SYMBOL } from "@/const/ui";

export const AED_TO_USD_RATE = 3.67;

export const convertAEDToUSD = (valueAED: number, rate: number) => {
  return valueAED / rate;
};

export const convertUSDToAED = (valueUSD: number, rate: number) => {
  return valueUSD * rate;
};

export const formatNumericValue = ({
  amount,
  minFractionDigits = 0,
  maxFractionDigits = 0,
}: {
  amount: number | string;
  minFractionDigits?: number;
  maxFractionDigits?: number;
}) => {
  const usedMaxFractionDigits = Math.max(maxFractionDigits, minFractionDigits);
  const amountNumber = typeof amount === "string" ? parseFloat(amount) : amount;

  return `${amountNumber.toLocaleString("en-US", {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: usedMaxFractionDigits,
  })}`;
};

export const isNumeric = (str: unknown) => {
  if (typeof str != "string") return false; // we only process strings!
  return (
    !isNaN(Number(str)) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
};

export function formatNumberShort(
  amount: number,
  currency: string,
  decimals: number = 1,
): string {
  const absAmount = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";

  let value: number;
  let suffix: string;

  if (amount === 0) {
    return `${sign}${currency}${0}`;
  }

  if (absAmount >= 1e9) {
    value = absAmount / 1e9;
    suffix = "B";
  } else if (absAmount >= 1e6) {
    value = absAmount / 1e6;
    suffix = "M";
  } else if (absAmount >= 1e3) {
    value = absAmount / 1e3;
    suffix = "K";
  } else {
    return `${amount.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }

  const formatted = value.toFixed(decimals);

  return `${sign}${currency}${formatted}${suffix}`;
}

export const convertAmountToSecondaryCurrency = (
  amount: number,
  currency: string,
  secondaryCurrency: string,
) => {
  if (
    [CURRENCY_3_LETTER.AED, CURRENCY_SYMBOL.AED].includes(currency) &&
    [CURRENCY_3_LETTER.USD, CURRENCY_SYMBOL.USD].includes(secondaryCurrency)
  ) {
    return convertAEDToUSD(amount, AED_TO_USD_RATE);
  } else if (
    [CURRENCY_3_LETTER.USD, CURRENCY_SYMBOL.USD].includes(currency) &&
    [CURRENCY_3_LETTER.AED, CURRENCY_SYMBOL.AED].includes(secondaryCurrency)
  ) {
    return convertUSDToAED(amount, AED_TO_USD_RATE);
  }
  return amount;
};

export const getSecondaryCurrency = (currency: string) => {
  if ([CURRENCY_3_LETTER.AED, CURRENCY_SYMBOL.AED].includes(currency)) {
    return CURRENCY_SYMBOL.USD;
  } else if ([CURRENCY_3_LETTER.USD, CURRENCY_SYMBOL.USD].includes(currency)) {
    return CURRENCY_3_LETTER.AED;
  }

  return CURRENCY_3_LETTER.AED;
};

interface IGetPercentage {
  part: number | undefined;
  total: number | undefined;
  fractionDigits?: number;
}

export const getPercentage = ({
  part,
  total,
  fractionDigits = 2,
}: IGetPercentage) => {
  if (!part || !total) {
    return 0;
  }

  return +parseFloat(((part / total) * 100)?.toString()).toFixed(
    fractionDigits
  );
};